import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth, getAuthHeaders } from "../utils/apiUtils";
import { API_URL } from "../config";
import { ensureCookieAccess, checkCookieAccess, clearCookieIssueFlags, setCookieIssueFlag } from "../utils/cookieHelper";
import { forceLogout } from "../utils/authUtils";
import { axios } from 'axios';

const ProfileContext = createContext();

const API_BASE_URL = API_URL; // Use centralized configuration

// Improved error handling utility function
const handleApiError = (error, customMessage = "Something went wrong!") => {
  
  if (error && error.response) {
    // Server responded with an error status (4xx, 5xx)
    if (error.response.status === 401) {
      const errorDetail = error.response.data?.detail || error.response.data?.message || "";
      
      // First check if it's a cookie issue
      if (errorDetail.toLowerCase().includes("cookie")) {
        // console.warn("Auth issue related to cookies detected");
        setCookieIssueFlag();
        // Don't log the user out if it's just a cookie issue
        toast.error("Authentication issue. Your browser may be blocking cookies.");
        return { error: true, message: "Cookie authentication issue", cookieIssue: true };
      }
      
      // Check for multiple device login
      if (errorDetail.toLowerCase().includes("another device")) {
        localStorage.removeItem("user");
        localStorage.removeItem("user_id");
        localStorage.removeItem("profileStats");
        
        // Show specific toast for multiple device login
        toast.warning("🔒 Multiple Device Login Detected", {
          toastId: 'multiple-device-login',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          render: () => (
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                🔒 Multiple Device Login Detected
              </div>
              <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                Your account is being used on another device. For security reasons, you have been logged out from this device.
              </div>
              <div style={{ fontSize: '12px', marginTop: '8px', color: '#888' }}>
                Please ensure you're only logged in on one device at a time.
              </div>
            </div>
          )
        });
        
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
        return { error: true, message: "Multiple device login detected" };
      }
      
      // Check for session invalidation
      if (errorDetail.toLowerCase().includes("invalidated")) {
        localStorage.removeItem("user");
        localStorage.removeItem("user_id");
        localStorage.removeItem("profileStats");
        toast.info("Your session has been invalidated. Please login again.");
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
        return { error: true, message: "Session invalidated" };
      }
      
      // Check for session refresh needed
      if (errorDetail.toLowerCase().includes("refresh")) {
        localStorage.removeItem("user");
        localStorage.removeItem("user_id");
        localStorage.removeItem("profileStats");
        toast.warning("Please refresh your browser or clear your browser data. Your session needs to be updated.");
        setTimeout(() => {
          window.location.href = "/";
        }, 3000);
        return { error: true, message: "Session refresh needed" };
      }
      
      // Standard session expiry case - log the user out
      localStorage.removeItem("user");
      localStorage.removeItem("user_id");
      localStorage.removeItem("profileStats");
      toast.info("Session expired. Please log in again.");
      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
      return { error: true, message: "Session expired" };
    } else if (error.response.status === 402) {
      toast.error(
        "Your subscription has expired. Please upgrade your plan to continue."
      );
      return {
        error: true,
        message: error.response.data?.detail || "Subscription expired!",
      };
    } else if (error.response.status === 404) {
      toast.error("Resource not found");
      return {
        error: true,
        message: error.response.data?.detail || "Resource not found",
      };
    } else {
      toast.error(error.response.data?.detail || customMessage);
      return {
        error: true,
        message: error.response.data?.detail || customMessage,
      };
    }
  } else if (error && error.request) {
    // Request was made but no response received (network error)
    toast.error("Network error. Please check your connection and try again.");
    return { error: true, message: "Network error" };
  } else {
    // Error occurred during request setup or it's a string message
    const errorMessage = typeof error === 'string' ? error : (error?.message || customMessage);
    toast.error(errorMessage);
    return { error: true, message: errorMessage };
  }
};

// Improved API fetch with cookie access handling
const fetchApi = async (url, options = {}) => {
  try {
    // Attempt to ensure cookie access before making the API call
    await ensureCookieAccess();
    
    // Add X-Requested-With header to help with CORS and SameSite cookies
    const headers = getAuthHeaders({
      ...options.headers,
      'X-Requested-With': 'XMLHttpRequest'
    });
    
    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Always include credentials
      headers 
    });
    
    // Check if we might have cookie issues
    if (response.status === 401) {
      try {
        // Parse response to see if it's cookie related
        const responseData = await response.clone().json();
        if (responseData.detail && (
            responseData.detail.includes('cookie') || 
            responseData.detail.includes('authentication')
          )) {
          // console.warn('Authentication issue detected - likely related to cookies');
          setCookieIssueFlag();
        }
      } catch (parseError) {
        // If we can't parse the response, check cookie access
        const cookiesEnabled = checkCookieAccess();
        if (!cookiesEnabled) {
          // Set cookie issue flag
          setCookieIssueFlag();
        }
      }
    }
    
    return response;
  } catch (error) {
    // console.error('Error in fetchApi:', error);
    throw error;
  }
};

// Utility function to perform API requests with error handling
const safeApiCall = async (apiCall, customErrorMessage) => {
  try {
    //check for cookie issue
    const cookieIssue = localStorage.getItem('cookie_issue');
    if(cookieIssue === 'true'){
      toast.info("Oops! It looks like third party cookies are blocked. please enable them and login again to continue using the app.",{
        autoClose: 5000,
        toastId: "cookie_issue",
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return { error: true, message: "Authentication cookies are missing or blocked, please enable them and login again." };
    }
    
    // Check if cookies are accessible first
    if (document.cookie.length > 0) {
      // If cookies are accessible now but we have cookie issue flags set,
      // clear them as the issue might be resolved
      if (localStorage.getItem('cookie_issue') === 'true' || 
          localStorage.getItem('cookie_auth_issue') === 'true') {
        clearCookieIssueFlags();
      }
    }
    
    const result = await apiCall();
    return { error: false, data: result.data || result, status: result.status };
  } catch (error) {
    // console.error("API Error:", error);
    
    // Check if the error is related to cookies/authentication
    if (error.message && (
        error.message.includes('cookie') || 
        error.message.includes('authentication')
      )) {
      setCookieIssueFlag();
    }
    
    return handleApiError(error, customErrorMessage);
  }
};

// New utility function to use fetchWithAuth
const safeFetchWithAuth = async (
  endpoint,
  options = {},
  customErrorMessage = "Something went wrong!"
) => {
  try {
    // Build headers with auth token for cookie-less auth (same as apiUtils)
    const baseHeaders = (options.body && options.body instanceof FormData)
      ? { ...options.headers }
      : { "Content-Type": "application/json", ...options.headers };
    const headers = getAuthHeaders(baseHeaders);

    const fetchOptions = {
      ...options,
      credentials: "include",
      headers,
    };

    // Make the API call with proper headers (including Authorization when token exists)
    const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);

    // Check for token expiration/auth issues
    if (response.status === 401) {
      localStorage.removeItem("user");
      window.location.href = "/";
      return { error: true, message: "Authentication failed. Please log in again." };
    }

    // Parse response
    let data;
    try {
      data = await response.json();
    } catch (error) {
      // console.error("Error parsing JSON response:", error);
      return { error: true, message: "Invalid response from server" };
    }

    // Handle error response
    if (!response.ok) {
      // For 422 Unprocessable Entity, provide more detailed error message
      if (response.status === 422 && data?.detail) {
        // console.error("Validation error:", data.detail);
        const errorMessage = Array.isArray(data.detail) 
          ? data.detail.map(err => `${err.loc[1]}: ${err.msg}`).join(', ')
          : data.detail;
        return { error: true, message: `Validation error: ${errorMessage}`, data };
      }
      
      return { error: true, message: data.detail || customErrorMessage, data };
    }

    return { error: false, data };
  } catch (error) {
    //  console.error(`API Error (${endpoint}):`, error);
    return { error: true, message: customErrorMessage };
  }
};

export const ProfileProvider = ({ children }) => {
  const IMAGE_LINK = "https://cdn.pumpgym.in/images";
  const [profileStats, setProfileStats] = useState({
    total_stats: {
      total_time: 0,
      total_calories: 0,
      total_sets: 0,
      total_reps: 0,
    },
    exercise_breakdown: {},
  });
  const [workoutPlansHistory, setWorkoutPlansHistory] = useState([]);
  const [workoutPage, setWorkoutPage] = useState(1);
  const [workoutPerPage, setWorkoutPerPage] = useState(10);
  const [dietPlansHistory, setDietPlansHistory] = useState([]);
  const [dietPage, setDietPage] = useState(1);
  const [dietPerPage, setDietPerPage] = useState(10);
  const [fetchedPages, setFetchedPages] = useState(0);
  const [workoutNeedsUpdate, setWorkoutNeedsUpdate] = useState(false);
  const [dietNeedsUpdate, setDietNeedsUpdate] = useState(false);
  const [exerciseHistory, setExerciseHistory] = useState([]);
  const [exerciseHistoryLoading, setExerciseHistoryLoading] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [sessionHistoryLoading, setSessionHistoryLoading] = useState(false);
  const [sessionHistoryPage, setSessionHistoryPage] = useState(1);
  const [sessionHistoryLimit, setSessionHistoryLimit] = useState(10);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [graphFilters, setGraphFilters] = useState({
    exerciseType: "",
    days: "",
    sortBy: "",
  });
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphData, setGraphData] = useState([]);

  // Add filters state to the context
  const [filters, setFilters] = useState({
    exerciseType: "",
    days: "",
    sortBy: "",
  });

  // Add authorization state - initialize from localStorage if available
  const [isAuthorized, setIsAuthorized] = useState(() => {
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        setIsAuthorized(userData.is_authorized !== undefined ? userData.is_authorized : false);
        return userData.is_authorized !== undefined ? userData.is_authorized : false;
      } catch (e) {
        return false;
      }
    }
    return false; // Default to false for security
  });
  
  const [isRestrictedAccess, setIsRestrictedAccess] = useState(() => {
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        setIsRestrictedAccess(userData.is_restricted_access !== undefined ? userData.is_restricted_access : true);
        return userData.is_restricted_access !== undefined ? userData.is_restricted_access : true;
      } catch (e) {
        return true;
      }
    }
    return true; // Default to true for security
  });

  const [myPlans, setMyPlans] = useState({
    workout: [],
    diet: [],
    lastFetched: null,
  });

  const [planCache, setPlanCache] = useState({});

  // Add this state to track plans across the application
  const [myPlansState, setMyPlansState] = useState({
    workout: [],
    diet: [],
  });
  const [plansUpdateTrigger, setPlansUpdateTrigger] = useState(0);

  // Add this after the fetchMyPlans function in the ProfileContext
  const [aiTrainerHistory, setAiTrainerHistory] = useState([]);
  const [aiTrainerHistoryLoading, setAiTrainerHistoryLoading] = useState(false);
  const [aiTrainerHistoryPage, setAiTrainerHistoryPage] = useState(1);
  const [aiTrainerHistoryLimit, setAiTrainerHistoryLimit] = useState(10);
  const [hasMoreAiTrainerHistory, setHasMoreAiTrainerHistory] = useState(true);
  const [selectedAiTrainerSession, setSelectedAiTrainerSession] = useState(null);
  const [aiTrainerSessionLoading, setAiTrainerSessionLoading] = useState(false);

  // Refs to prevent duplicate API calls
  const isFetchingProfileRef = useRef(false);
  const hasCheckedAuthRef = useRef(false);

  // Add useEffect to detect cookie issues on mount
  useEffect(() => {
    const detectAuthIssues = async () => {
      // Only run this check if the user is logged in
      const userStr = localStorage.getItem("user");
      if (!userStr) return;
      
      // Prevent duplicate checks
      if (hasCheckedAuthRef.current) return;
      hasCheckedAuthRef.current = true;
      
      // Check if we're coming from Google Auth
      const checkAfterAuth = localStorage.getItem('check_cookie_after_auth') === 'true';
      const checkTimestamp = localStorage.getItem('check_cookie_timestamp');
      
      // If we're coming from Google Auth and the timestamp is recent (within 30 seconds)
      if (checkAfterAuth && checkTimestamp) {
        const now = Date.now();
        const timestamp = parseInt(checkTimestamp);
        if (!isNaN(timestamp) && now - timestamp < 30000) { // 30 seconds
          
          // Clear the check flags
          localStorage.removeItem('check_cookie_after_auth');
          localStorage.removeItem('check_cookie_timestamp');
          
          // Delay the check to avoid race conditions
          // Note: Don't fetch profile here - let components that need it call fetchUserProfile
          // This avoids duplicate API calls. Just check cookie access.
          setTimeout(async () => {
            try {
              // Just check if cookies are accessible, don't make API calls
              const cookiesEnabled = checkCookieAccess();
              if (!cookiesEnabled) {
                setCookieIssueFlag();
              } else {
                // Cookies are accessible, clear any flags
                // The actual profile fetch will be done by components that need it
                clearCookieIssueFlags();
              }
            } catch (error) {
              // console.error("Error in post-auth cookie check:", error);
              setCookieIssueFlag();
            }
          }, 2000); // 2 second delay
          
          return; // Skip the rest of the checks
        }
      }
      
      // Regular cookie check for normal app use - only check cookie access, don't fetch profile
      // The profile will be fetched by HomePage or other components using fetchUserProfile
      try {
        // Check if cookies are accessible
        const cookiesEnabled = checkCookieAccess();
        if (!cookiesEnabled) {
          // console.warn("Cookies appear to be blocked on application mount");
          setCookieIssueFlag();
          return;
        }
        
        // Don't make a profile API call here - let fetchUserProfile handle it
        // Just check cookie access and set flags accordingly
        // The actual profile fetch will be done by components that need it
      } catch (error) {
        // console.error("Error checking authentication on mount:", error);
        
        // If error is likely cookie-related, set the flag
        if (error.message && (
            error.message.includes('cookie') || 
            error.message.includes('authentication')
          )) {
          setCookieIssueFlag();
        }
      }
    };
    
    detectAuthIssues();
  }, []);

  const fetchProfileStats = async (forceRefresh = false) => {
    try {
      // Check if we have cached data and it's not a forced refresh
      const cachedStats = localStorage.getItem("profileStats");
      if (cachedStats && !forceRefresh) {
        const parsedStats = JSON.parse(cachedStats);
        setProfileStats(parsedStats);

        // Process exercise breakdown into history format
        if (parsedStats.exercise_breakdown) {
          const exercises = Object.entries(parsedStats.exercise_breakdown).map(
            ([name, stats]) => ({
              name,
              ...stats,
              id: generateExerciseId(name, stats),
            })
          );
          setExerciseHistory(exercises);
        }

        return parsedStats;
      }

      setExerciseHistoryLoading(true);

      try {
        // Ensure cookie access before making the request
        await ensureCookieAccess();
        
        const response = await fetchApi(`${API_BASE_URL}/profile-stats`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            // Try to determine if it's a cookie issue
            try {
              const errorData = await response.json();
              if (errorData.detail && (
                  errorData.detail.includes('cookies') || 
                  errorData.detail.includes('authentication')
                )) {
                // console.warn("Cookie issue detected when fetching profile stats");
                setCookieIssueFlag();
              }
            } catch (parseError) {
              // If we can't parse the response, check for cookie access
              if (!checkCookieAccess()) {
                setCookieIssueFlag();
              }
            }
          }
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch profile stats");
        }

        const data = await response.json();
        setProfileStats(data);

        // Process exercise breakdown into history format
        if (data.exercise_breakdown) {
          const exercises = Object.entries(data.exercise_breakdown).map(
            ([name, stats]) => ({
              name,
              ...stats,
              id: generateExerciseId(name, stats),
            })
          );
          setExerciseHistory(exercises);
        }

        // Cache the response
        localStorage.setItem("profileStats", JSON.stringify(data));
        localStorage.setItem("profileStatsTimestamp", Date.now().toString());
        
        // If we successfully fetched profile stats, clear any cookie issue flags
        clearCookieIssueFlags();
        
        return data;
      } catch (fetchError) {
        // console.error("Error fetching profile stats:", fetchError);
        
        // Check if it's a cookie-related error
        if (fetchError.message && (
            fetchError.message.includes('cookie') || 
            fetchError.message.includes('authentication')
          )) {
          //  console.warn("Cookie issue detected in profile stats fetch");
          setCookieIssueFlag();
        }
        
        // If we have cached data, use that as a fallback
        if (cachedStats) {
          const parsedStats = JSON.parse(cachedStats);
          setProfileStats(parsedStats);
          return parsedStats;
        }
        
        throw fetchError; // Re-throw to be caught by the outer catch
      }
    } catch (error) {
      // console.error("Error in fetchProfileStats:", error);
      setExerciseHistoryLoading(false);
      
      // Check cached data as fallback
      const cachedStats = localStorage.getItem("profileStats");
      if (cachedStats) {
        try {
          const parsedStats = JSON.parse(cachedStats);
          setProfileStats(parsedStats);
          return parsedStats;
        } catch (parseError) {
          // console.error("Error parsing cached stats:", parseError);
        }
      }
      
      return null;
    } finally {
      setExerciseHistoryLoading(false);
    }
  };

  //for clear cache data
  const clearCache = () => {
    setProfileStats(null);
    setWorkoutPlansHistory([]);
    setDietPlansHistory([]);
    setExerciseHistory([]);
    setSessionHistory([]);
    setMyPlans({ workout: [], diet: [] });
    setPlanCache({});
    setMyPlansState({ workout: [], diet: [] });
    setGraphData([]);
    setGraphFilters({ exerciseType: "", days: "", sortBy: "" });
    setGraphLoading(false);
    setFetchedPages(0);
    setWorkoutNeedsUpdate(false);
    setDietNeedsUpdate(false);
    setExerciseHistoryLoading(false);
    setSessionHistoryLoading(false);
    setSessionHistoryPage(1);
    setSessionHistoryLimit(10);
    setHasMoreSessions(true);
    setFilters({ exerciseType: "", days: "", sortBy: "" });

    // Clear localStorage cache as well
    localStorage.removeItem("last_auth_check");
    localStorage.removeItem('user');
    localStorage.removeItem('user_profile_timestamp');
    localStorage.removeItem('profileStats');
    localStorage.removeItem('profileStatsTimestamp');
    localStorage.removeItem('workout_profile');
    localStorage.removeItem('workout_profile_timestamp');
    localStorage.removeItem('no_workout_profile');
    localStorage.removeItem('no_workout_profile_timestamp');
    localStorage.removeItem('onboarding_dismissed_timestamp');
    localStorage.removeItem('onboarding_dismissed_user_id');
    localStorage.removeItem('user_id');
    localStorage.removeItem('hasRated');
    localStorage.removeItem('exerciseModalTourSeen');
    localStorage.removeItem('profile_prompt_dismissed_user_id');
    localStorage.removeItem('profile_prompt_dismissed_timestamp');
  };

  // Add function to refresh authorization status
  const refreshAuthorizationStatus = async () => {
    
    // Force refresh user profile to get latest authorization status
    const userData = await fetchUserProfile(true);
    
    if (userData) {
      // Update authorization states
      setIsAuthorized(userData.is_authorized !== undefined ? userData.is_authorized : false);
      setIsRestrictedAccess(userData.is_restricted_access !== undefined ? userData.is_restricted_access : true);
      
      // Clear all cached data to ensure fresh data is loaded with new permissions
      clearCache();
      
      return userData.is_authorized;
    }
    
    return false;
  };

  // Add function to check if user needs authorization refresh
  const checkAuthorizationRefresh = () => {
    const lastAuthCheck = localStorage.getItem("user_profile_timestamp");
    const currentTime = Date.now();
    const AUTH_CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes
    
    return !lastAuthCheck || (currentTime - parseInt(lastAuthCheck) > AUTH_CHECK_INTERVAL);
  };

  const fetchGraphData = async (filters = {}) => {
    setGraphLoading(true);

    const { days = 7, exerciseType = "" } = filters;

    // Build query parameters
    let queryParams = `days=${days}`;
    if (exerciseType) queryParams += `&exercise_type=${exerciseType}`;

    // Use fetchWithAuth directly instead of safeApiCall
    const result = await fetchWithAuth(`/exercise-history?${queryParams}`, {
      method: "GET"
    }, "Failed to load exercise history");

    setGraphLoading(false);

    if (!result.error) {
      setGraphData(result.data);
      return result.data;
    }

    setGraphData([]);
    return [];
  };

  //delete session by id
  const deleteSessionById = async (sessionId) => {
    try {
      const result = await safeFetchWithAuth(
        `/delete-session/${sessionId}`,
        { method: "DELETE" },
        "Failed to delete session"
      );

      if (!result.error) {
        // Don't show toast here - let the component handle the success animation
        // Update session history by removing the deleted session
        setSessionHistory((prevSessions) =>
          prevSessions.filter((session) => session.id !== sessionId)
        );
        
        // Also refresh profile stats to update totals
        await fetchProfileStats(true);
        
        return true;
      } else {
        // console.error("Delete session failed:", result.message);
        toast.error(result.message || "Failed to delete session");
        return false;
      }
    } catch (error) {
      // console.error("Error deleting session:", error);
      toast.error("Failed to delete session");
      return false;
    }
  };

  const fetchUserProfile = async (forceRefresh = false) => {
    // Prevent concurrent calls
    if (isFetchingProfileRef.current) {
      // If already fetching, wait a bit and return cached data if available
      const cachedUser = localStorage.getItem("user");
      if (cachedUser) {
        try {
          return JSON.parse(cachedUser);
        } catch (e) {
          // If cached data is invalid, wait for the ongoing fetch
        }
      }
      // Wait for the ongoing fetch to complete
      let attempts = 0;
      while (isFetchingProfileRef.current && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      // After waiting, try to return cached data
      const cachedUserAfterWait = localStorage.getItem("user");
      if (cachedUserAfterWait) {
        try {
          return JSON.parse(cachedUserAfterWait);
        } catch (e) {
          return null;
        }
      }
      return null;
    }

    try {
      //only fetch user profile if user is logged in
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        return null;
      }

      // If user data is in localStorage and not a forced refresh, use that
      const userProfileTimestamp = localStorage.getItem("user_profile_timestamp");
      const cachedUser = localStorage.getItem("user");
      
      const currentTime = Date.now();
      const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
      
      // Check if we should force refresh due to potential authorization changes
      const lastAuthCheck = localStorage.getItem("user_profile_timestamp");
      const AUTH_CHECK_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes for auth changes
      const shouldCheckAuthStatus = !lastAuthCheck || (currentTime - parseInt(lastAuthCheck) > AUTH_CHECK_INTERVAL);
      
      if (
        cachedUser && 
        !forceRefresh && 
        !shouldCheckAuthStatus &&
        userProfileTimestamp && 
        currentTime - parseInt(userProfileTimestamp) < CACHE_DURATION
      ) {
        return JSON.parse(cachedUser);
      }
      
      // Mark as fetching to prevent concurrent calls
      isFetchingProfileRef.current = true;
      
      // Ensure cookie access before fetching profile
      await ensureCookieAccess();
      
      try {
        // Fetch fresh profile data
        const response = await fetchApi(`${API_BASE_URL}/profile`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            console.warn("Auth issue when fetching profile - could be a cookie issue");
            
            // Try to get more details from the response
            try {
              const errorData = await response.json();
              if (
                  errorData.detail.includes('Authentication token is missing.')
                ) {
                console.warn("Cookie-related authentication issue confirmed:", errorData.detail);
                setCookieIssueFlag();
                clearCache();
                return null;
              }
            } catch (parseError) {
              // If we can't parse the response, assume it's a cookie issue
              console.warn("Couldn't parse auth error response, assuming cookie issue");
              setCookieIssueFlag();
            }
            
            // If unauthorized but cookies might be blocked, don't log out immediately
            const cookiesEnabled = checkCookieAccess();
            if (!cookiesEnabled) {
              console.warn('Cookies appear to be blocked. Using cached user data if available.');
              if (cachedUser) {
                return JSON.parse(cachedUser);
              }
            }
            
            // Clear cached user data for true auth failures
            localStorage.removeItem("user");
            localStorage.removeItem("user_id");
            localStorage.removeItem("user_profile_timestamp");
            localStorage.removeItem("last_auth_check");
            // Force a clean logout for genuine unauthorized sessions
            forceLogout({ redirectTo: "/", reason: `profile_http_${response.status}` });
            return null;
          }
          
          // For other errors, try to get error details
          try {
            const errorData = await response.json();
            throw new Error(`Failed to fetch user profile: ${errorData.detail || 'Unknown error'}`);
          } catch (parseError) {
            throw new Error('Failed to fetch user profile');
          }
        }

        const userData = await response.json();

        // If the backend marks the user as unauthorized/restricted, force logout immediately.
        // This prevents the UI from continuing in a stale "logged in" state.
        const isAdminUser = (userData?.user_type || "").toLowerCase() === "admin";
        if (!isAdminUser && userData?.is_restricted_access === true && userData?.is_authorized === false) {
          toast.error("Your access has been revoked. Please login again.", { toastId: "access-revoked" });
          forceLogout({ redirectTo: "/", reason: "access_revoked" });
          isFetchingProfileRef.current = false;
          return null;
        }
        
        // Update localStorage
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("user_profile_timestamp", currentTime.toString());
        
        // Update authorization state
        // setIsAuthorized(userData.is_authorized !== undefined ? userData.is_authorized : false);
        // setIsRestrictedAccess(userData.is_restricted_access !== undefined ? userData.is_restricted_access : true);
        
        // Profile fetch succeeded, clear any cookie issue flags
        clearCookieIssueFlags();
        
        isFetchingProfileRef.current = false;
        return userData;
      } catch (fetchError) {
        // Handle network errors or parsing errors specifically
        //  console.error("Error during profile fetch operation:", fetchError);
        
        // If the error message suggests it's cookie-related, set the flag
        if (fetchError.message && (
            fetchError.message.includes('cookie') || 
            fetchError.message.includes('authentication')
          )) {
          console.warn("Setting cookie issue flag due to fetch error");
          setCookieIssueFlag();
        }
        
        isFetchingProfileRef.current = false;
        throw fetchError; // Re-throw to be caught by the outer catch
      }
    } catch (error) {
      isFetchingProfileRef.current = false;
      // Set cookie issue flag when fetch fails with specific conditions
      if (
        (error.message && (
          error.message.includes("authentication") || 
          error.message.includes("cookies") ||
          error.message.includes("unauthorized")
        )) ||
        (error.response && error.response.status === 401)
      ) {
        console.warn("Setting cookie issue flag due to auth error");
        setCookieIssueFlag();
      } else {
        // More general error that might be API related
      }
      
      // If we have cached user data, use that as a fallback
      const cachedUser = localStorage.getItem("user");
      if (cachedUser) {
        return JSON.parse(cachedUser);
      }
      
      return null;
    }
  };

  const checkSubscriptionExpiry = () => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user || !user.expiry_date) {
      return false;
    }

    const expiryDate = new Date(user.expiry_date);
    const currentDate = new Date();

    return expiryDate < currentDate;
  };

  const updateWorkoutNeedsUpdate = (value = true) => {
    setWorkoutNeedsUpdate(value);
  };

  const updateDietNeedsUpdate = (value = true) => {
    setDietNeedsUpdate(value);
  };

  const updateTokensLeft = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    user.tokens_left -= 1;
    localStorage.setItem("user", JSON.stringify(user));
  };

  const fetchWorkoutProfile = async (forceRefresh = false) => {

    //do not fetch profile if user is not logged in
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      return;
    }

    // Check for cache first if not forcing refresh
    const cachedWorkoutProfile = localStorage.getItem('workout_profile');
    const cachedTimestamp = localStorage.getItem('workout_profile_timestamp');
    const noProfileCached = localStorage.getItem('no_workout_profile');
    const noProfileTimestamp = localStorage.getItem('no_workout_profile_timestamp');
    const currentTime = Date.now();
    const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

    // If we know the user doesn't have a workout profile and it's still fresh, return null
    if (!forceRefresh && noProfileCached && noProfileTimestamp) {
      if (currentTime - parseInt(noProfileTimestamp) < CACHE_DURATION) {
        return null;
      } else {
        // Cache expired, remove the "no profile" markers
        localStorage.removeItem('no_workout_profile');
        localStorage.removeItem('no_workout_profile_timestamp');
      }
    }

    if (!forceRefresh && cachedWorkoutProfile && cachedTimestamp) {
      if (currentTime - parseInt(cachedTimestamp) < CACHE_DURATION) {
        // Return cached data if still valid
        try {
          return JSON.parse(cachedWorkoutProfile);
        } catch (e) {
          // If cached data is corrupted, remove it and fetch fresh
          localStorage.removeItem('workout_profile');
          localStorage.removeItem('workout_profile_timestamp');
        }
      }
    }

    try {
      const result = await safeFetchWithAuth('/workout-profile', {
        method: 'GET',
      }, 'Failed to fetch workout profile');

      if (result) {
        // Clear any "no profile" cache since we got data
        localStorage.removeItem('no_workout_profile');
        localStorage.removeItem('no_workout_profile_timestamp');
        
        // Cache the successful result
        localStorage.setItem('workout_profile', JSON.stringify(result));
        localStorage.setItem('workout_profile_timestamp', currentTime.toString());
        return result;
      } else {
        // This means we got a null result, likely from a 404
        // Cache that the user doesn't have a workout profile
        localStorage.setItem('no_workout_profile', 'true');
        localStorage.setItem('no_workout_profile_timestamp', currentTime.toString());
        
        // Remove any old workout profile cache
        localStorage.removeItem('workout_profile');
        localStorage.removeItem('workout_profile_timestamp');
        
        return null;
      }
    } catch (error) {
      // Check if it's a 404 error (no workout profile exists)
      if (error.error.includes('404') || error.error.includes('not found')) {
        // Cache that the user doesn't have a workout profile
        localStorage.setItem('no_workout_profile', 'true');
        localStorage.setItem('no_workout_profile_timestamp', currentTime.toString());
        
        // Remove any old workout profile cache
        localStorage.removeItem('workout_profile');
        localStorage.removeItem('workout_profile_timestamp');
        
        return null;
      }

      // Handle 404 as a normal case (user doesn't have workout profile yet)
      if (error.data && error.data.detail && error.data.detail.includes("404")) {
        // Cache that the user doesn't have a workout profile
        localStorage.setItem('no_workout_profile', 'true');
        localStorage.setItem('no_workout_profile_timestamp', currentTime.toString());
        
        // Remove any old workout profile cache
        localStorage.removeItem('workout_profile');
        localStorage.removeItem('workout_profile_timestamp');
        
        return null;
      }

      throw error;
    }
  };

  const updateUserProfile = async (formData) => {
    // Check for cookie issues first
    await ensureCookieAccess();
    
    // Use fetchWithAuth instead of direct fetch to handle cookie issues
    const result = await safeFetchWithAuth(
      "/update-profile",
      {
        method: "PUT",
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary for FormData
      },
      "Failed to update profile"
    );

    if (!result.error) {
      // Update local storage with new user data
      localStorage.setItem("user", JSON.stringify(result.data));

      return result.data;
    }

    return null;
  };

  const updateTokens = async () => {
    try {
      const result = await safeFetchWithAuth(
        `${API_BASE_URL}/reduce-token`,
        { method: "POST" },
        "Failed to update tokens"
      );

      if (result.error) return false;

      // Update user data in localStorage with reduced tokens
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData && userData.tokens_left > 0) {
        userData.tokens_left -= 1;
        localStorage.setItem("user", JSON.stringify(userData));
      }

      return true;
    } catch (error) { 
      return false;
    }
  };

  // Add this new function to update stats directly with new workout data
  const updateStatsWithWorkout = (workoutData) => {
    // just remove the profileStats from local storage so that it can be fetched again
    localStorage.removeItem("profileStats");
  };

  // Diet Plan Functions
  const generateDietPlan = async (dietData) => {
    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/generate-diet-plan`, {
        method: "POST",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(dietData),
        credentials: "include", // Important for cookies
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to generate diet plan");
      }

      return response.json();
    }, "Failed to generate diet plan");

    if (!result.error) {
      setDietNeedsUpdate(true);
      toast.success("Diet plan generated successfully!");
      return result.data;
    }

    return null;
  };

  //fetch diet plans history
  const fetchDietPlans = async (resetHistory = false) => {
    const result = await safeApiCall(async () => {
      const response = await fetch(
        `${API_BASE_URL}/diet_plans?page=${dietPage}&limit=${dietPerPage}`,
        {
          method: "GET",
          credentials: "include", // Important for cookies
          headers: getAuthHeaders({
            "Content-Type": "application/json",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch diet plans");
      }

      return response.json();
    }, "Failed to fetch diet plans");

    if (!result.error) {
      const data = result.data;

      // If resetHistory is true or it's page 1, replace the entire history
      // Otherwise, append the new plans with deduplication
      setDietPlansHistory((prevPlans) => {
        if (resetHistory || dietPage === 1) {
          return data.plans;
        }

        // Filter out duplicates before appending
        const newPlans = data.plans.filter(
          (newPlan) =>
            !prevPlans.some(
              (existingPlan) => existingPlan.plan_id === newPlan.plan_id
            )
        );

        return [...prevPlans, ...newPlans];
      });

      return data;
    }

    return null;
  };

  const deleteDietPlan = async (planId) => {
    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/diet_plan/${planId}`, {
        method: "DELETE",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete diet plan");
      }

      return response.json();
    }, "Failed to delete diet plan");

    if (!result.error) {
      // Update the diet plans history by filtering out the deleted plan
      setDietPlansHistory((prevHistory) =>
        prevHistory.filter((plan) => plan.plan_id !== planId)
      );

      toast.success("Diet plan deleted successfully");
      return true;
    }

    return false;
  };

  // Workout Plan Functions
  const generateWorkoutPlan = async (workoutData) => {
    // Check for cookie issues first
    await ensureCookieAccess();
    
    // Use fetchWithAuth instead of direct fetch to handle cookie issues
    const result = await fetchWithAuth(
      '/generate-workout-plan',
      {
        method: "POST",
        body: JSON.stringify(workoutData),
      },
      "Failed to generate workout plan"
    );

    if (!result.error) {
      setWorkoutNeedsUpdate(true);
      toast.success("Workout plan generated successfully!");
      return result.data;
    }

    return null;
  };

  const fetchWorkoutPlans = async () => {
    const result = await safeApiCall(async () => {
      const response = await fetch(
        `${API_BASE_URL}/workout_plans?page=${workoutPage}&limit=${workoutPerPage}`,
        {
          method: "GET",
          credentials: "include", // Important for cookies
          headers: getAuthHeaders({
            "Content-Type": "application/json",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch workout plans");
      }

      return response.json();
    }, "Failed to fetch workout plans");

    if (!result.error) {
      const data = result.data;

      // For page 1, replace the entire history
      if (workoutPage === 1) {
        setWorkoutPlansHistory(data.plans);
      } else {
        // For subsequent pages, append new plans
        setWorkoutPlansHistory((prevPlans) => {
          // Filter out duplicates
          const newPlans = data.plans.filter(
            (newPlan) =>
              !prevPlans.some((existingPlan) => existingPlan.plan_id === newPlan.plan_id)
          );
          return [...prevPlans, ...newPlans];
        });
      }
      return data;
    }

    return null;
  };

  const deleteWorkoutPlan = async (planId) => {
    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/workout_plan/${planId}`, {
        method: "DELETE",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete workout plan");
      }

      return response.json();
    }, "Failed to delete workout plan");

    if (!result.error) {
      // Remove the deleted plan from the state
      setWorkoutPlansHistory((prevPlans) =>
        prevPlans.filter((plan) => plan.plan_id !== planId)
      );
      toast.success("Workout plan deleted successfully");
      return true;
    }

    return false;
  };

  //function for reset workout plans history
  const resetWorkoutPlansHistory = () => {
    setWorkoutPlansHistory([]);
    setDietPlansHistory([]);
    setProfileStats({});
    setExerciseHistory([]);
    setDietPage(1);
    setWorkoutPage(1);
    setDietPerPage(10);
    setWorkoutPerPage(10);
    setFetchedPages(0);
    setWorkoutNeedsUpdate(false);
    setDietNeedsUpdate(false);
  };

  // Helper function to generate a unique ID for each exercise
  const generateExerciseId = (name, stats) => {
    return `${name}-${stats.sets}-${stats.reps}-${
      stats.duration
    }-${Date.now()}`;
  };

  // Add a new function to update exercise stats when a new exercise is completed
  const updateExerciseStats = async (exerciseData) => {
    try {
      const result = await safeFetchWithAuth(
        `${API_BASE_URL}/update-exercise-metrics`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(exerciseData),
        },
        "Failed to update exercise statistics"
      );

      if (result.error) return false;

      // Refresh the profile stats
      await fetchProfileStats(true);

      return true;
    } catch (error) {
      return false;
    }
  };

  // Add function to manually add an exercise session
  const addManualExercise = async (exerciseData) => {
    try {
      // Create FormData from the exercise data
      const formData = new FormData();
      formData.append('exercise_type', exerciseData.exercise_type);
      formData.append('duration', exerciseData.duration.toString());
      formData.append('reps', exerciseData.reps.toString());
      formData.append('sets', exerciseData.sets.toString());
      formData.append('calories', exerciseData.calories.toString());
      
      // Add date if provided
      if (exerciseData.date) {
        formData.append('date', exerciseData.date);
      }

      // Check for cookie issues first
      await ensureCookieAccess();
      
      // Use fetchWithAuth instead of direct fetch to handle cookie issues
      const result = await fetchWithAuth(
        '/add-manual-exercise',
        {
          method: 'POST',
          body: formData,
        },
        "Failed to add manual exercise"
      );

      if (!result.error) {
        toast.success("Exercise added successfully!");
        
        // Refresh exercise session history
        await fetchSessionHistory({ reset: true });
        
        // Also refresh profile stats to update totals
        await fetchProfileStats(true);
        
        // Refresh graph data if available
        if (typeof fetchGraphData === 'function') {
          await fetchGraphData();
        }
        
        return true;
      }
      
      return false;
    } catch (error) { 
      return false;
    }
  };

  // Add a function to get exercise history with filtering options
  const getExerciseHistory = (filters = {}) => {
    if (!exerciseHistory.length) return [];

    let filteredHistory = [...exerciseHistory];

    // Apply filters if provided
    if (filters.exerciseName) {
      filteredHistory = filteredHistory.filter((ex) =>
        ex.name.toLowerCase().includes(filters.exerciseName.toLowerCase())
      );
    }

    if (filters.minReps) {
      filteredHistory = filteredHistory.filter(
        (ex) => ex.reps >= filters.minReps
      );
    }

    if (filters.minSets) {
      filteredHistory = filteredHistory.filter(
        (ex) => ex.sets >= filters.minSets
      );
    }

    // Sort options
    if (filters.sortBy) {
      switch (filters.sortBy) {
        case "calories":
          filteredHistory.sort((a, b) => b.calories - a.calories);
          break;
        case "duration":
          filteredHistory.sort((a, b) => b.duration - a.duration);
          break;
        case "reps":
          filteredHistory.sort((a, b) => b.reps - a.reps);
          break;
        case "sets":
          filteredHistory.sort((a, b) => b.sets - a.sets);
          break;
        default:
          // Default sort by name
          filteredHistory.sort((a, b) => a.name.localeCompare(b.name));
      }
    }

    return filteredHistory;
  };

  // Add function to clear exercise history
  const clearExerciseHistory = async () => {
    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/clear-exercise-history`, {
        method: "POST",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to clear exercise history");
      }

      return response.json();
    }, "Failed to clear exercise history");

    if (!result.error) {
      // Update local state
      setExerciseHistory([]);

      // Update profile stats
      const updatedStats = {
        ...profileStats,
        exercise_breakdown: {},
        total_stats: {
          total_time: 0,
          total_calories: 0,
          total_sets: 0,
          total_reps: 0,
        },
      };

      setProfileStats(updatedStats);
      localStorage.setItem("profileStats", JSON.stringify(updatedStats));

      // toast.success("Exercise history cleared successfully");
      return true;
    }

    return false;
  };

  // Add function to fetch session history with pagination and filters
  const fetchSessionHistory = async (options = {}) => {
    const {
      exercise_type = null,
      exerciseType = null, // Keep for backward compatibility
      days = null, // Make days optional and not applied by default
      page = sessionHistoryPage,
      limit = sessionHistoryLimit,
      reset = false,
      returnAllData = true, // New parameter to control what data is returned
    } = options;

    // If reset flag is true, we'll clear existing data
    if (reset) {
      setSessionHistory([]);
    }

    // Check if this page might already be loaded
    const dataAlreadyLoaded =
      !reset &&
      sessionHistory.length > 0 &&
      sessionHistory.length >= page * limit;

    // If we already have this page loaded and reset isn't requested, just return the existing data
    if (dataAlreadyLoaded && returnAllData) {
      return sessionHistory;
    }

    setSessionHistoryLoading(true);

    // Calculate offset based on page and limit
    const offset = (page - 1) * limit;

    // Use exercise_type parameter or fall back to exerciseType for backward compatibility
    const finalExerciseType = exercise_type || exerciseType;

    // Build query parameters - only add days if explicitly provided
    let queryParams = `limit=${limit}&offset=${offset}`;
    if (finalExerciseType) queryParams += `&exercise_type=${finalExerciseType}`;
    // Only add days filter if explicitly provided (not null/undefined)
    if (days !== null && days !== undefined) queryParams += `&days=${days}`;

    const result = await safeApiCall(async () => {
      const response = await fetch(
        `${API_BASE_URL}/session-history?${queryParams}`,
        {
          method: "GET",
          credentials: "include", // Important for cookies
          headers: getAuthHeaders({
            "Content-Type": "application/json",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch session history");
      }
      return response.json();
    }, "Failed to fetch session history");

    setSessionHistoryLoading(false);

    if (!result.error) {
      const newSessions = result.data;

      // Check if we've reached the end of the data
      setHasMoreSessions(newSessions.length === limit);

      // Update session history state
      setSessionHistory((prevSessions) => {
        // For page 1 with reset, replace the entire history
        if (reset && page === 1) {
          return newSessions;
        }

        // For subsequent pages or when preserving data, merge without duplicates
        const sessionMap = new Map();

        // Add existing sessions to map
        prevSessions.forEach((session) => {
          sessionMap.set(session.id, session);
        });

        // Add or update with new sessions
        newSessions.forEach((session) => {
          sessionMap.set(session.id, session);
        });

        // Convert map back to array and sort by date (newest first)
        return Array.from(sessionMap.values()).sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
      });

      // Return either just the new data or all accumulated data
      return returnAllData ? sessionHistory : newSessions;
    }

    setSessionHistoryLoading(false);
    return returnAllData ? sessionHistory : [];
  };

  // Improve the loadMoreSessions function
  const loadMoreSessions = async (options = {}) => {
    if (sessionHistoryLoading || !hasMoreSessions) return [];

    const nextPage = sessionHistoryPage + 1;
    setSessionHistoryPage(nextPage);

    // Load more data and return the complete accumulated dataset
    return await fetchSessionHistory({
      ...options,
      page: nextPage,
      reset: false,
      returnAllData: true,
    });
  };

  // Add function to reset session history pagination
  const resetSessionHistory = () => {
    setSessionHistory([]);
    setSessionHistoryPage(1);
    setHasMoreSessions(true);
  };

  // Add these functions to your context
  const addToMyPlans = async (planData) => {
    // Check for cookie issues first
    await ensureCookieAccess();
    
    // Use fetchWithAuth instead of direct fetch to handle cookie issues
    const result = await fetchWithAuth(
      `/add-to-my-plans`,
      {
        method: "POST",
        body: JSON.stringify(planData),
      },
      "Failed to add plan"
    );

    if (!result.error) {
      return result.data;
    }

    return null;
  };

  const updatePlanStatus = async (planId, status) => {
    // Check for cookie issues first
    await ensureCookieAccess();
    
    // Use fetchWithAuth instead of direct fetch to handle cookie issues
    const result = await fetchWithAuth(
      `/update-plan-status/${planId}`,
      {
        method: "PUT",
        body: JSON.stringify({ status }),
      },
      "Failed to update plan status"
    );

    if (!result.error) {
      // Invalidate cache
      setPlanCache((prev) => {
        const newCache = { ...prev };
        Object.keys(newCache).forEach((key) => {
          if (key.startsWith("my-plans")) {
            delete newCache[key];
          }
        });
        return newCache;
      });

      // toast.success("Plan status updated successfully");
      return result.data;
    }

    return null;
  };

  // Update the updatePlanItemStatus function to be more efficient
  const updatePlanItemStatus = async (itemId, newStatus) => {
    // Check if this is a redundant update (same status)
    const cachedPlan = Object.values(planCache).find((cache) =>
      cache.data?.items?.some(
        (item) => item.id === itemId && item.status === newStatus
      )
    );

    if (cachedPlan) {
      return { success: true, message: "Status already up to date" };
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/update-item-status/${itemId}?status=${newStatus}`,
        {
          method: "PUT",
          credentials: "include", // Important for cookies
          headers: getAuthHeaders({
            "Content-Type": "application/json",
          }),
        }
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      // Update all related plan caches
      Object.keys(planCache).forEach((key) => {
        const cache = planCache[key];
        if (cache.data?.items?.some((item) => item.id === itemId)) {
          cache.data.items = cache.data.items.map((item) =>
            item.id === itemId ? { ...item, status: newStatus } : item
          );
        }
      });

      return data;
    } catch (error) {
      //  console.error("Error updating plan item status:", error);
      toast.error("Failed to update item status");
      return null;
    }
  };

  // Add function to get plan details
  const getPlanDetails = async (planId, planType) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/${planType}-plan/${planId}`,
        {
          method: "GET",
          credentials: "include", // Important for cookies
          headers: getAuthHeaders({
            "Content-Type": "application/json",
          }),
        }
      );

      if (!response.ok) {
        // console.error(
        //   `Failed to fetch ${planType} plan details:`,
        //   response.status
        // );
        return null;
      }

      return await response.json();
    } catch (error) {
      // console.error(`Error fetching ${planType} plan details:`, error);
      toast.error(`Failed to fetch ${planType} plan details`);
      return null;
    }
  };

  const fetchPlanDetails = async (planType, planId) => {
    const result = await safeApiCall(async () => {
      const response = await fetch(
        `${API_BASE_URL}/${planType}-plan/${planId}/details`,
        {
          method: "GET",
          credentials: "include", // Important for cookies
          headers: getAuthHeaders({
            "Content-Type": "application/json",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch ${planType} plan details`);
      }

      return response.json();
    }, `Failed to fetch ${planType} plan details`);

    if (!result.error) {
      return result.data;
    }

    return null;
  };

  // Add these methods to your context provider
  const addToMyWorkoutPlan = async (planData) => {
    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/add-to-my-workout-plan`, {
        method: "POST",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(planData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to add workout plan");
      }

      return response.json();
    }, "Failed to add workout plan");

    if (!result.error) {
      // toast.success("Workout plan added to your schedule");
      return result.data;
    }

    throw new Error("Failed to add workout plan");
  };

  const addToMyDietPlan = async (planData) => {
    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/add-to-my-diet-plan`, {
        method: "POST",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(planData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to add diet plan");
      }

      return response.json();
    }, "Failed to add diet plan");

    if (!result.error) {
      // toast.success("Diet plan added to your schedule");
      return result.data;
    }

    throw new Error("Failed to add diet plan");
  };

  // Enhanced fetchMyPlans function with caching
  const fetchMyPlans = async (planType = null, forceRefresh = false) => {
    const cacheKey = planType ? `my-plans-${planType}` : "my-plans-all";
    const cachedData = planCache[cacheKey];
    const cacheExpiry = 5 * 60 * 1000; // 5 minutes

    // Return cached data if it exists and is valid (always return array for callers)
    if (
      !forceRefresh &&
      cachedData &&
      Date.now() - cachedData.timestamp < cacheExpiry
    ) {
      const raw = cachedData.data;
      const arr = Array.isArray(raw) ? raw : (raw?.plans ?? []);
      if (planType) return arr.filter(plan => plan.planType === planType);
      return arr;
    }

    let url = `${API_BASE_URL}/my-plans`;
    const queryParams = [];
    if (planType) queryParams.push(`plan_type=${planType}`);
    // Optional: pass today's date (YYYY-MM-DD) for day-wise plans and new-day reset
    const today = new Date().toISOString().slice(0, 10);
    queryParams.push(`date=${today}`);
    if (queryParams.length) url += `?${queryParams.join("&")}`;

    const result = await safeApiCall(async () => {
      const response = await fetch(url, {
        method: "GET",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch plans");
      }

      return response.json();
    }, "Failed to fetch plans");

    if (!result.error) {
      // API returns { plans, byDay, date }; support legacy array response
      const plansArray = Array.isArray(result.data)
        ? result.data
        : (result.data?.plans ?? []);

      // Cache the result (store full response so byDay/date available if needed)
      setPlanCache((prev) => ({
        ...prev,
        [cacheKey]: {
          data: result.data,
          timestamp: Date.now(),
        },
      }));

      // Update myPlansState with the fresh data
      if (planType) {
        const forType = (plansArray || []).filter(plan => plan.planType === planType);
        setMyPlansState((prevState) => {
          const newState = {
            ...prevState,
            [planType]: forType
          };
          console.log(`fetchMyPlans - Updated ${planType} plans:`, forType?.length || 0);
          return newState;
        });
      } else {
        const workoutPlans = (plansArray || []).filter(plan => plan.planType === 'workout');
        const dietPlans = (plansArray || []).filter(plan => plan.planType === 'diet');
        setMyPlansState((prevState) => {
          const newState = {
            ...prevState,
            workout: workoutPlans,
            diet: dietPlans
          };
          console.log(`fetchMyPlans - Updated all plans - Workout: ${workoutPlans.length}, Diet: ${dietPlans.length}`);
          return newState;
        });
      }

      // Trigger a re-render for components that depend on plans
      setPlansUpdateTrigger(prev => prev + 1);

      // Return array so callers (e.g. loadPlans, deleteUserPlan refresh) get plans, not { plans, byDay, date }
      if (planType) {
        return (plansArray || []).filter(plan => plan.planType === planType);
      }
      return plansArray || [];
    }

    throw new Error("Failed to fetch plans");
  };

  const deleteUserPlan = async (planId) => {
    console.log('deleteUserPlan called with planId:', planId, 'Type:', typeof planId);
    
    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/user-plan/${planId}`, {
        method: "DELETE",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete plan");
      }

      return response.json();
    }, "Failed to delete plan");

    if (!result.error) {
      console.log('deleteUserPlan - API call successful, result:', result);
      
      // Invalidate the cache for my-plans and plan details
      setPlanCache((prev) => {
        const newCache = { ...prev };
        // Remove any cached entries related to my-plans and plan details
        Object.keys(newCache).forEach((key) => {
          if (key.startsWith("my-plans") || key.includes("-user-plan-details")) {
            delete newCache[key];
          }
        });
        return newCache;
      });

      toast.success(result.data.message || "Plan deleted successfully");
      
      // Force refresh my plans data to ensure UI is updated
      try {
        // Refresh both plan types to ensure UI is updated with fresh data from server
        const workoutData = await fetchMyPlans('workout', true);
        const dietData = await fetchMyPlans('diet', true);
        
        console.log('Plans refreshed after deletion - Workout:', workoutData?.length, 'Diet:', dietData?.length);
      } catch (error) {
        console.error('Error refreshing plans after deletion:', error);
        
        // If refresh fails, manually update the state to remove the deleted plan
        setMyPlansState((prevState) => {
          console.log('deleteUserPlan - Manual state update after refresh failure');
          const updatedState = { ...prevState };
          ['workout', 'diet'].forEach(planType => {
            if (updatedState[planType]) {
              updatedState[planType] = updatedState[planType].filter(
                plan => {
                  const planUserPlanId = String(plan.userPlanId);
                  const planPlanId = String(plan.planId);
                  const targetPlanId = String(planId);
                  return !(planUserPlanId === targetPlanId || planPlanId === targetPlanId);
                }
              );
            }
          });
          return updatedState;
        });
        
        // Trigger a re-render for components that depend on plans
        setPlansUpdateTrigger(prev => prev + 1);
      }
      
      return true;
    }

    return false;
  };

  // Add these functions to your ProfileContext
  const checkUserRating = async () => {
    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/check-rating`, {
        method: "GET",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      });

      // if (!response.ok) {
      //   throw new Error("Failed to check rating");
      // }
      if(response.ok){
        return response.json();
      }
      return { has_rated: false };

      return response.json();
    }, "Failed to check rating");

    if (!result.error) {
      localStorage.setItem("hasRated", result.data.has_rated);
      return result.data;
    }

    return { has_rated: false };
  };

  const submitRating = async (rating, feedback = "") => {
    const formData = new FormData();
    formData.append("rating", rating);
    formData.append("feedback", feedback);

    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/add-rating`, {
        method: "POST",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to submit rating");
      }

      return response.json();
    }, "Failed to submit rating");

    if (!result.error) {
      localStorage.setItem("hasRated", result.data.has_rated);
      return result.data;
    }

    return null;
  };

  const updateRating = async (rating, feedback = "") => {
    const formData = new FormData();
    formData.append("rating", rating);
    formData.append("feedback", feedback);

    const result = await safeApiCall(async () => {
      const response = await fetch(`${API_BASE_URL}/update-rating`, {
        method: "PUT",
        credentials: "include", // Important for cookies
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update rating");
      }

      return response.json();
    }, "Failed to update rating");

    if (!result.error) {
      return result.data;
    }

    return null;
  };

  // Add this function to update a specific item's status across components
  const updateMyPlanItemStatus = async (
    planId,
    itemId,
    newStatus,
    planType
  ) => {
    // Update myPlansState
    setMyPlansState((prevState) => {
      // Make a copy of the plans for the specified type
      const plans = [...(prevState[planType] || [])];

      // Find and update the specific plan and item
      const updatedPlans = plans.map((plan) => {
        if (plan.userPlanId.toString() === planId.toString()) {
          return {
            ...plan,
            items: plan.items.map((item) =>
              item.id.toString() === itemId.toString()
                ? { ...item, status: newStatus }
                : item
            ),
          };
        }
        return plan;
      });

      return {
        ...prevState,
        [planType]: updatedPlans,
      };
    });

    // Also update plan cache to maintain consistency (cache.data can be array or { plans, byDay, date })
    setPlanCache((prev) => {
      const newCache = { ...prev };

      Object.keys(newCache).forEach((key) => {
        if (key.startsWith(`my-plans-${planType}`) || key === "my-plans-all") {
          const cachedData = newCache[key];
          if (!cachedData || !cachedData.data) return;
          const raw = cachedData.data;
          const plansArray = Array.isArray(raw) ? raw : (raw?.plans ?? []);
          if (!Array.isArray(plansArray) || plansArray.length === 0) return;

          const updatedPlans = plansArray.map((plan) => {
            if (plan.userPlanId?.toString() === planId.toString()) {
              return {
                ...plan,
                items: Array.isArray(plan.items)
                  ? plan.items.map((item) =>
                      item.id?.toString() === itemId.toString()
                        ? { ...item, status: newStatus }
                        : item
                    )
                  : plan.items,
              };
            }
            return plan;
          });

          newCache[key] = {
            ...cachedData,
            data: Array.isArray(raw) ? updatedPlans : { ...raw, plans: updatedPlans },
          };
        }
      });

      return newCache;
    });
  };

  const fetchAiTrainerHistory = async (options = {}) => {
    try {
      setAiTrainerHistoryLoading(true);
      
      const { page = aiTrainerHistoryPage, limit = aiTrainerHistoryLimit, resetHistory = false } = options;
      
      // If resetting history, clear existing data and reset page
      if (resetHistory) {
        setAiTrainerHistory([]);
        setAiTrainerHistoryPage(1);
        setHasMoreAiTrainerHistory(true);
      }
      
      // Build query params
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      const response = await safeFetchWithAuth(`${API_BASE_URL}/ai-trainer-history?${params.toString()}`);
      
      if (response) {
        const { history, total, pages } = response;
        
        // If this is not a reset (i.e., it's a "load more" operation), append to existing data
        const updatedHistory = resetHistory ? history : [...aiTrainerHistory, ...history];
        
        setAiTrainerHistory(updatedHistory);
        setAiTrainerHistoryPage(page + 1);
        setHasMoreAiTrainerHistory(page < pages);
        
        return { history: updatedHistory, hasMore: page < pages };
      }
      
      return { history: aiTrainerHistory, hasMore: false };
    } catch (error) {
      toast.error("Failed to load AI trainer history");
      return { history: aiTrainerHistory, hasMore: false };
    } finally {
      setAiTrainerHistoryLoading(false);
    }
  };

  const loadMoreAiTrainerHistory = async () => {
    if (!hasMoreAiTrainerHistory || aiTrainerHistoryLoading) return;
    
    await fetchAiTrainerHistory({
      page: aiTrainerHistoryPage,
      limit: aiTrainerHistoryLimit
    });
  };

  const resetAiTrainerHistory = () => {
    setAiTrainerHistory([]);
    setAiTrainerHistoryPage(1);
    setHasMoreAiTrainerHistory(true);
  };

  const fetchAiTrainerSessionDetail = async (planId) => {
    try {
      setAiTrainerSessionLoading(true);
      
      const response = await safeFetchWithAuth(`${API_BASE_URL}/ai-trainer-history/${planId}`);
      
      if (response) {
        setSelectedAiTrainerSession(response);
        return response;
      }
      
      return null;
    } catch (error) {
      //  console.error("Error fetching AI trainer session detail:", error);
      toast.error("Failed to load AI trainer session details");
      return null;
    } finally {
      setAiTrainerSessionLoading(false);
    }
  };

  const clearNoWorkoutProfileCache = () => {
    localStorage.removeItem('no_workout_profile');
    localStorage.removeItem('no_workout_profile_timestamp');
    localStorage.removeItem('workout_profile');
    localStorage.removeItem('workout_profile_timestamp');
  };

  // Authorization helpers
  const isFeatureAuthorized = () => {
    // If restricted access is disabled, everyone is authorized
    // if (!isRestrictedAccess) return true;
    
    // Check if user is authorized or is admin (from localStorage)
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userIsAuthorized = user.is_authorized !== undefined ? user.is_authorized : false;
    
    // Update state for consistency, but don't rely on it for return value
    setIsAuthorized(userIsAuthorized);
    
    // Return true if user is admin or authorized
    if (user.user_type === 'admin') return true;
    
    // Return the actual authorization status from localStorage
    return userIsAuthorized;
  };

  const getRestrictedMessage = () => {
    if (!isRestrictedAccess) return null;
    if (isAuthorized) return null;
    
    return "This feature is currently restricted. Please contact admin for access.";
  };

  return (
    <ProfileContext.Provider
      value={{
        profileStats,
        setProfileStats,
        fetchProfileStats,
        fetchUserProfile,
        updateUserProfile,
        updateStatsWithWorkout,
        API_BASE_URL,
        checkSubscriptionExpiry,
        updateTokensLeft,
        updateTokens,
        dietPlansHistory,
        workoutPlansHistory,
        dietPage,
        setDietPage,
        dietPerPage,
        setDietPerPage,
        workoutPage,
        setWorkoutPage,
        workoutPerPage,
        setWorkoutPerPage,
        generateDietPlan,
        fetchDietPlans,
        deleteDietPlan,
        generateWorkoutPlan,
        fetchWorkoutPlans,
        deleteWorkoutPlan,
        fetchedPages,
        setFetchedPages,
        workoutNeedsUpdate,
        setWorkoutNeedsUpdate,
        dietNeedsUpdate,
        setDietNeedsUpdate,
        updateWorkoutNeedsUpdate,
        setDietPlansHistory,
        setWorkoutPlansHistory,
        updateDietNeedsUpdate,
        resetWorkoutPlansHistory,
        exerciseHistory,
        exerciseHistoryLoading,
        updateExerciseStats,
        addManualExercise,
        getExerciseHistory,
        clearExerciseHistory,
        sessionHistory,
        sessionHistoryLoading,
        sessionHistoryPage,
        setSessionHistoryPage,
        sessionHistoryLimit,
        setSessionHistoryLimit,
        hasMoreSessions,
        fetchSessionHistory,
        loadMoreSessions,
        resetSessionHistory,
        // Add the new filter state to the context
        filters,
        setFilters,
        deleteSessionById,
        addToMyPlans,
        fetchMyPlans,
        updatePlanStatus,
        getPlanDetails,
        myPlans,
        fetchPlanDetails,
        addToMyWorkoutPlan,
        addToMyDietPlan,
        updatePlanItemStatus,
        deleteUserPlan,
        graphFilters,
        setGraphFilters,
        graphLoading,
        graphData,
        fetchGraphData,
        checkUserRating,
        submitRating,
        updateRating,
        planCache,
        setPlanCache,
        updateMyPlanItemStatus,
        myPlansState,
        setMyPlansState,
        plansUpdateTrigger,
        clearCache,
        aiTrainerHistory,
        aiTrainerHistoryLoading,
        aiTrainerHistoryPage,
        aiTrainerHistoryLimit,
        hasMoreAiTrainerHistory,
        selectedAiTrainerSession,
        aiTrainerSessionLoading,
        fetchAiTrainerHistory,
        loadMoreAiTrainerHistory,
        resetAiTrainerHistory,
        fetchAiTrainerSessionDetail,
        fetchWorkoutProfile,
        IMAGE_LINK,
        clearNoWorkoutProfileCache,
        // Authorization state and helpers
        isAuthorized,
        isRestrictedAccess,
        isFeatureAuthorized,
        getRestrictedMessage,
        refreshAuthorizationStatus,
        checkAuthorizationRefresh,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfileStats = () => useContext(ProfileContext);
